import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import type { EmployeeSummary, MilestoneView, PageResponse } from "../../shared/types/api";
import { formatSlippage } from "./projectVocabulary";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Adds or edits a milestone (§105).
 *
 * <p>The baseline date is shown but never editable. It is what every slippage figure is measured
 * against, and a form that let it be re-typed would turn a project that had moved twice into one
 * that had never moved — which is exactly the report §105 exists to prevent.
 */
export function MilestoneFormDrawer({
  projectId,
  milestone,
  open,
  onClose,
  onSaved,
}: {
  projectId: string;
  milestone: MilestoneView | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [currentDate, setCurrentDate] = useState(today());
  const [forecastDate, setForecastDate] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setName(milestone?.name ?? "");
    setDescription(milestone?.description ?? "");
    setCurrentDate(milestone?.currentDate ?? today());
    setForecastDate(milestone?.forecastDate ?? "");
    setOwnerId(milestone?.ownerId ?? "");
    setError(null);
  }, [open, milestone]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    const body = {
      name,
      description: description || null,
      currentDate,
      forecastDate: forecastDate || null,
      ownerId: ownerId || null,
    };
    try {
      if (milestone) {
        await api.patch(`/api/v1/projects/${projectId}/milestones/${milestone.id}`, body);
      } else {
        await api.post(`/api/v1/projects/${projectId}/milestones`, body);
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the milestone.");
    } finally {
      setSaving(false);
    }
  };

  const baselineDate = milestone?.baselineDate ?? null;
  const movedFromBaseline = baselineDate !== null && currentDate !== baselineDate;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={milestone ? "Edit milestone" : "Add milestone"}
      subtitle={milestone?.name}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={name.trim() === "" || currentDate === ""}
          >
            {saving ? "Saving…" : milestone ? "Save changes" : "Add milestone"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Name" required>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Customer UAT sign-off"
          />
        </Field>

        <Field label="Description" hint="Optional. What counts as reaching it.">
          <Textarea
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="All P1 defects closed and the customer has signed the acceptance note."
          />
        </Field>

        {baselineDate && (
          <div className="flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 px-3 py-2.5 text-sm">
            <span className="text-ink-500">Committed at baseline</span>
            <span className="tnum text-ink-900">{baselineDate}</span>
          </div>
        )}

        <Field
          label="Planned date"
          required
          hint="What the current plan says. The committed date above never changes."
        >
          <Input
            type="date"
            value={currentDate}
            onChange={(event) => setCurrentDate(event.target.value)}
          />
        </Field>

        {movedFromBaseline && baselineDate && (
          <InlineAlert tone="warning">
            This moves the milestone {formatSlippage(daysBetween(baselineDate, currentDate))}{" "}
            against what was committed. The commitment stays as it was, and the difference will be
            reported as slippage.
          </InlineAlert>
        )}

        <Field
          label="Forecast date"
          hint="Optional. Where it is actually expected to land, if that differs from the plan."
        >
          <Input
            type="date"
            value={forecastDate}
            onChange={(event) => setForecastDate(event.target.value)}
          />
        </Field>

        <Field label="Owner" hint="Who is accountable for reaching it.">
          <Select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
            <option value="">Nobody assigned</option>
            {(employees.data?.content ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName} — {employee.designation}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Drawer>
  );
}

/**
 * Records a milestone as reached, on the date it was actually reached.
 *
 * <p>The date is asked for rather than assumed to be today: milestones are often recorded a few
 * days after the fact, and defaulting silently would quietly overstate the slippage.
 */
export function AchieveMilestoneDrawer({
  projectId,
  milestone,
  onClose,
  onSaved,
}: {
  projectId: string;
  milestone: MilestoneView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [actualDate, setActualDate] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (milestone) {
      setActualDate(today());
      setError(null);
    }
  }, [milestone]);

  const submit = async () => {
    if (!milestone) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/projects/${projectId}/milestones/${milestone.id}/achieve`, {
        actualDate,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not record that.");
    } finally {
      setSaving(false);
    }
  };

  const against = milestone?.baselineDate ?? milestone?.currentDate ?? actualDate;
  const slippage = daysBetween(against, actualDate);

  return (
    <Drawer
      open={Boolean(milestone)}
      onClose={onClose}
      title="Record as achieved"
      subtitle={milestone?.name}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {saving ? "Saving…" : "Record as achieved"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Reached on" required hint="The date it was actually reached, not today.">
          <Input
            type="date"
            value={actualDate}
            onChange={(event) => setActualDate(event.target.value)}
          />
        </Field>

        <div className="overflow-hidden rounded-lg border border-ink-200">
          <Row
            label={milestone?.baselineDate ? "Committed at baseline" : "Planned"}
            value={against}
          />
          <Row label="Reached" value={actualDate} />
          <Row
            label="Slippage"
            value={formatSlippage(slippage)}
            tone={slippage > 0 ? "bad" : "good"}
          />
        </div>
      </div>
    </Drawer>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad";
}) {
  const colour =
    tone === "bad" ? "text-red-600" : tone === "good" ? "text-emerald-600" : "text-ink-900";
  return (
    <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 text-sm last:border-0">
      <span className="text-ink-500">{label}</span>
      <span className={`tnum font-medium ${colour}`}>{value}</span>
    </div>
  );
}

/**
 * Whole days from one ISO date to another.
 *
 * <p>Both are plain calendar dates with no time component, so parsing them as UTC midnight keeps
 * the difference exact — a local-time parse would land a day out whenever a daylight-saving change
 * falls between them.
 */
function daysBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return 0;
  }
  return Math.round((end - start) / 86_400_000);
}
